<template>
  <div class="uk-container full-page">
    <UserHeader />

    <div class="register-kit-shell">
      <section class="register-kit-card">
        <h1>Register Your Test Kit</h1>
        <p class="register-kit-intro">
          Enter the Kit ID from your collection bag, then share only the details needed for the person or pet using the kit.
        </p>

        <div v-if="errorMessage" class="form-alert">{{ errorMessage }}</div>

        <form class="register-kit-step" @submit.prevent="validateKitId" novalidate>
          <label class="field-label" for="serialNumber">Kit ID <span class="field-meta">Required</span></label>
          <input
            id="serialNumber"
            v-model="formattedSerialNumber"
            class="uk-input serial-number"
            :class="{ error: serialTouched && !isValidSerialNumber && formattedSerialNumber.length > 0 }"
            type="text"
            inputmode="text"
            autocomplete="off"
            placeholder="XXXXXX-XXXXXX"
            @input="formatSerialNumber"
            @blur="serialTouched = true"
          />
          <div v-if="serialTouched && formattedSerialNumber && !isValidSerialNumber" class="error">
            Enter the Kit ID in the format XXXXXX-XXXXXX.
          </div>
          <div class="register-kit-sample-bar-code">
            <div class="register-kit-sample-image">
              <img src="/images/sample-kitId-bar-code.png" alt="Example showing where to find the Kit ID on your collection bag" />
              <span class="register-kit-sample-overlay">Sample</span>
            </div>
          </div>
          <button class="uk-button uk-button-primary" type="submit" :disabled="validatingKit || !isValidSerialNumber">
            <span v-if="validatingKit" uk-spinner="ratio: 0.6" class="uk-margin-small-right"></span>
            {{ validatingKit ? 'Checking kit...' : 'Continue' }}
          </button>
        </form>

        <form v-if="isValidKit" class="register-kit-step register-kit-details" @submit.prevent="submitUserInfo" novalidate>
          <div class="section-header">
            <h3>{{ detailSectionTitle }}</h3>
            <p>{{ detailSectionDescription }}</p>
          </div>

          <div class="uk-grid-small" uk-grid>
            <div class="uk-width-1-2@s">
              <label class="field-label" for="kit-first-name">{{ firstNameLabel }} <span class="field-meta">Required</span></label>
              <input
                id="kit-first-name"
                v-model.trim="form.firstName"
                class="uk-input"
                :class="fieldClass('firstName')"
                type="text"
                autocomplete="given-name"
                @blur="markTouched('firstName')"
                @input="markTouched('firstName')"
              />
              <div v-if="showError('firstName')" class="error">{{ fieldError('firstName') }}</div>
            </div>

            <div class="uk-width-1-2@s">
              <label class="field-label" for="kit-last-name">{{ lastNameLabel }} <span class="field-meta">Required</span></label>
              <input
                id="kit-last-name"
                v-model.trim="form.lastName"
                class="uk-input"
                :class="fieldClass('lastName')"
                type="text"
                autocomplete="family-name"
                @blur="markTouched('lastName')"
                @input="markTouched('lastName')"
              />
              <div v-if="showError('lastName')" class="error">{{ fieldError('lastName') }}</div>
            </div>
          </div>

          <div v-if="form.subjectType === 'human'" class="subject-section">
            <div class="uk-grid-small" uk-grid>
              <div class="uk-width-1-2@s">
                <label class="field-label" for="human-gender">Gender <span class="field-meta">Optional</span></label>
                <select
                  id="human-gender"
                  v-model="form.gender"
                  class="uk-select"
                  :class="fieldClass('gender')"
                  @blur="markTouched('gender')"
                  @change="markTouched('gender')"
                >
                  <option value="">Select gender</option>
                  <option v-for="option in humanGenderOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <div v-if="showError('gender')" class="error">{{ fieldError('gender') }}</div>
              </div>

              <div v-if="form.gender === 'self_describe'" class="uk-width-1-2@s">
                <label class="field-label" for="human-gender-self">Describe Gender <span class="field-meta">Required</span></label>
                <input
                  id="human-gender-self"
                  v-model.trim="form.genderSelfDescribe"
                  class="uk-input"
                  :class="fieldClass('genderSelfDescribe')"
                  type="text"
                  @blur="markTouched('genderSelfDescribe')"
                  @input="markTouched('genderSelfDescribe')"
                />
                <div v-if="showError('genderSelfDescribe')" class="error">{{ fieldError('genderSelfDescribe') }}</div>
              </div>
            </div>

            <div class="uk-grid-small" uk-grid>
              <div class="uk-width-1-2@s">
                <label class="field-label" for="human-birth-month">Birth Month <span class="field-meta">Optional</span></label>
                <select
                  id="human-birth-month"
                  v-model="form.birthMonth"
                  class="uk-select"
                  :class="fieldClass('birthMonth')"
                  @blur="markTouched('birthMonth')"
                  @change="markTouched('birthMonth')"
                >
                  <option value="">Select month</option>
                  <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
                </select>
                <div v-if="showError('birthMonth')" class="error">{{ fieldError('birthMonth') }}</div>
              </div>
              <div class="uk-width-1-2@s">
                <label class="field-label" for="human-birth-year">Birth Year <span class="field-meta">Optional</span></label>
                <input
                  id="human-birth-year"
                  v-model="form.birthYear"
                  class="uk-input"
                  :class="fieldClass('birthYear')"
                  type="text"
                  inputmode="numeric"
                  maxlength="4"
                  placeholder="YYYY"
                  @blur="markTouched('birthYear')"
                  @input="handleBirthYearInput"
                />
                <div v-if="showError('birthYear')" class="error">{{ fieldError('birthYear') }}</div>
              </div>
            </div>
          </div>

          <div v-if="form.subjectType === 'pet'" class="subject-section">
            <fieldset class="pet-type-fieldset" :class="{ error: showError('petType') }">
              <legend>
                Which pet is this kit for?
                <span class="field-meta">Required</span>
              </legend>
              <p class="pet-type-help">Select one to continue with the pet details.</p>

              <div class="pet-type-options">
                <label v-for="option in petTypeOptions" :key="option.value" class="pet-type-option">
                  <input
                    class="pet-type-radio"
                    type="radio"
                    :value="option.value"
                    v-model="form.petType"
                    @change="handlePetTypeChange"
                  />
                  <span class="pet-type-card">
                    <span class="pet-type-icon" aria-hidden="true">
                      {{ option.value === 'dog' ? '🐶' : '🐱' }}
                    </span>
                    <span class="pet-type-label">{{ option.label }}</span>
                    <span v-if="form.petType === option.value" class="pet-type-selected">
                      <span aria-hidden="true">✓</span> Selected
                    </span>
                  </span>
                </label>
              </div>
            </fieldset>
            <div v-if="showError('petType')" class="error">{{ fieldError('petType') }}</div>

            <div v-if="form.petType" class="pet-details">
              <div class="uk-grid-small" uk-grid>
                <div class="uk-width-1-2@s">
                  <label class="field-label" for="pet-name">Pet Name <span class="field-meta">Required</span></label>
                  <input
                    id="pet-name"
                    v-model.trim="form.petName"
                    class="uk-input"
                    :class="fieldClass('petName')"
                    type="text"
                    autocomplete="off"
                    @blur="markTouched('petName')"
                    @input="markTouched('petName')"
                  />
                  <div v-if="showError('petName')" class="error">{{ fieldError('petName') }}</div>
                </div>

                <div class="uk-width-1-2@s">
                  <label class="field-label" for="pet-breed">Breed <span class="field-meta">Optional</span></label>
                  <input
                    id="pet-breed"
                    v-model.trim="form.petBreed"
                    class="uk-input"
                    :class="fieldClass('petBreed')"
                    type="text"
                    :list="petBreedListId"
                    :placeholder="petBreedPlaceholder"
                    @blur="markTouched('petBreed')"
                    @input="handlePetBreedInput"
                  />
                  <datalist :id="petBreedListId">
                    <option v-for="breed in filteredPetBreedOptions" :key="breed" :value="breed">
                      {{ breed }}
                    </option>
                  </datalist>
                  <div v-if="showError('petBreed')" class="error">{{ fieldError('petBreed') }}</div>
                </div>
              </div>

              <div v-if="showOtherBreedField" class="uk-margin-top">
                <label class="field-label" for="pet-breed-other">Other Breed</label>
                <input
                  id="pet-breed-other"
                  v-model.trim="form.petBreedOther"
                  class="uk-input"
                  type="text"
                  placeholder="Enter breed if known"
                  @blur="markTouched('petBreedOther')"
                  @input="markTouched('petBreedOther')"
                />
              </div>

              <div class="uk-margin-top">
                <label class="field-label" for="pet-approximate-age">Approximate Age <span class="field-meta">Optional</span></label>
                <input
                  id="pet-approximate-age"
                  v-model.trim="form.petApproximateAge"
                  class="uk-input"
                  :class="fieldClass('petApproximateAge')"
                  type="text"
                  placeholder="Example: 2 years"
                  @blur="markTouched('petApproximateAge')"
                  @input="markTouched('petApproximateAge')"
                />
                <div v-if="showError('petApproximateAge')" class="error">{{ fieldError('petApproximateAge') }}</div>
              </div>

              <div class="registration-summary" role="status">
                <span class="registration-summary-icon" aria-hidden="true">
                  {{ form.petType === 'dog' ? '🐶' : '🐱' }}
                </span>
                <span>
                  This kit will be registered for
                  <strong>{{ form.petName || 'your pet' }}</strong>
                  as a <strong>{{ form.petType }}</strong>.
                </span>
              </div>
            </div>
          </div>

          <button class="uk-button uk-button-primary uk-margin-top" type="submit" :disabled="submitDisabled">
            <span v-if="isSubmitting" uk-spinner="ratio: 0.5" class="uk-margin-small-right"></span>
            {{ isSubmitting ? 'Registering...' : 'Register Kit' }}
          </button>
        </form>
      </section>
    </div>

    <UcariFooter />
  </div>
</template>

<script>
import axios from 'axios';
import UserHeader from './UserHeader.vue';
import UcariFooter from './UcariFooter.vue';
import {
  buildKitRegistrationPayload,
  catBreedOptions,
  createInitialKitRegistrationForm,
  dogBreedOptions,
  humanGenderOptions,
  petTypeOptions,
  validateKitRegistrationForm
} from '@/utils/registrationForms';

const PET_TEST_KIT_TYPE_IDS = [4, 9, 11];

export default {
  name: 'RegisterKit',
  components: {
    UserHeader,
    UcariFooter
  },
  data() {
    return {
      formattedSerialNumber: '',
      isValidKit: false,
      kitId: '',
      testKitTypeId: '',
      validatingKit: false,
      isSubmitting: false,
      errorMessage: '',
      cid: '',
      accessToken: '',
      isValidSerialNumber: false,
      serialTouched: false,
      formSubmitted: false,
      touched: {},
      maxDate: new Date().toISOString().substr(0, 10),
      humanGenderOptions,
      petTypeOptions,
      dogBreedOptions,
      catBreedOptions,
      months: [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
      ],
      form: createInitialKitRegistrationForm()
    };
  },
  created() {
    document.title = 'Register Kit | UCARI';
    this.accessToken = localStorage.getItem('accessToken');
    this.cid = localStorage.getItem('cid');
  },
  computed: {
    formErrors() {
      return validateKitRegistrationForm(this.form);
    },
    isPetKit() {
      return this.form.subjectType === 'pet';
    },
    detailSectionTitle() {
      return this.isPetKit ? 'Owner and Pet Details' : 'Personal Details';
    },
    detailSectionDescription() {
      return this.isPetKit
        ? 'Enter the owner name, then complete the pet profile and age details for this kit.'
        : 'Complete the demographic fields for the person using this kit.';
    },
    firstNameLabel() {
      return this.isPetKit ? 'Owner First Name' : 'First Name';
    },
    lastNameLabel() {
      return this.isPetKit ? 'Owner Last Name' : 'Last Name';
    },
    filteredPetBreedOptions() {
      if (this.form.petType === 'dog') return this.dogBreedOptions;
      if (this.form.petType === 'cat') return this.catBreedOptions;
      return [];
    },
    petBreedListId() {
      return `pet-breed-options-${this.form.petType || 'all'}`;
    },
    petBreedPlaceholder() {
      if (!this.form.petType) return 'Select dog or cat first';
      return `Search ${this.form.petType} breeds`;
    },
    showOtherBreedField() {
      return this.form.petBreed === 'Other / Unknown';
    },
    submitDisabled() {
      return this.isSubmitting || Object.keys(this.formErrors).length > 0;
    }
  },
  methods: {
    fieldError(field) {
      return this.formErrors[field] || '';
    },
    fieldClass(field) {
      return { error: this.showError(field) };
    },
    showError(field) {
      return (this.touched[field] || this.formSubmitted) && !!this.fieldError(field);
    },
    markTouched(field) {
      this.touched = { ...this.touched, [field]: true };
    },
    handlePetTypeChange() {
      this.form.petBreed = '';
      this.form.petBreedOther = '';
      this.markTouched('petType');
    },
    handlePetBreedInput() {
      if (this.form.petBreed !== 'Other / Unknown') {
        this.form.petBreedOther = '';
      }
      this.markTouched('petBreed');
    },
    formatSerialNumber(event) {
      let input = event.target.value;
      input = input.replace(/[^A-Za-z0-9]/g, '').substring(0, 12);
      if (input.length > 6) {
        input = `${input.slice(0, 6)}-${input.slice(6)}`;
      }
      this.formattedSerialNumber = input;
      this.validateSerialNumber();
    },
    validateSerialNumber() {
      this.isValidSerialNumber = /^[A-Za-z0-9]{6}-[A-Za-z0-9]{6}$/.test(this.formattedSerialNumber);
    },
    handleBirthYearInput(event) {
      this.form.birthYear = event.target.value.replace(/\D/g, '').slice(0, 4);
      this.markTouched('birthYear');
    },
    async validateKitId() {
      this.serialTouched = true;
      this.errorMessage = '';
      if (!this.isValidSerialNumber) {
        return;
      }

      this.validatingKit = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/validate-kit`,
          { serialNumber: this.formattedSerialNumber },
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
              cid: this.cid
            }
          }
        );

        if (response.data.isValid) {
          this.isValidKit = true;
          this.testKitTypeId = response.data.testKitTypeId;
          this.kitId = response.data.kitId;
          this.form.subjectType = PET_TEST_KIT_TYPE_IDS.includes(response.data.testKitTypeId) ? 'pet' : 'human';
        } else {
          this.isValidKit = false;
          this.errorMessage = 'Invalid Kit ID.';
        }
      } catch (error) {
        if (error.response?.data?.error === 'Already Registered') {
          this.errorMessage = 'Kit ID is already registered.';
        } else {
          this.errorMessage = 'An error occurred while validating the kit.';
        }
      } finally {
        this.validatingKit = false;
      }
    },
    async submitUserInfo() {
      if (this.isSubmitting) return;
      this.formSubmitted = true;
      this.errorMessage = '';

      if (this.submitDisabled) {
        return;
      }

      this.isSubmitting = true;
      try {
        const payload = buildKitRegistrationPayload({
          ...this.form,
          kitId: this.kitId
        });
        await axios.post(`${process.env.VUE_APP_API_URL}/api/register-kit`, payload, {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            cid: this.cid
          },
          method: 'POST'
        });
        this.$router.push('/kit-registered');
      } catch (error) {
        this.errorMessage = `Failed to register kit: ${error.response?.data?.error || error.message}`;
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.register-kit-shell {
  display: flex;
  justify-content: center;
  padding: 24px 0 40px;
}

.register-kit-card {
  box-sizing: border-box;
  width: 100%;
  max-width: 860px;
  padding: 28px;
  border: 1px solid #d7dfd2;
  border-radius: 20px;
  background: #ffffff;
}

.register-kit-card h1 {
  min-width: 0;
  max-width: none;
  margin-top: 0;
  overflow: visible;
}

.register-kit-step {
  box-sizing: border-box;
}

.register-kit-intro,
.section-header p {
  color: #536051;
}

.register-kit-step + .register-kit-step {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #d7dfd2;
}

.register-kit-sample-image {
  position: relative;
  max-width: 380px;
}

.register-kit-sample-image img {
  border-radius: 14px;
}

.field-label {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.field-meta {
  font-size: 12px;
  font-weight: 500;
  color: #71806e;
}

.subject-section {
  margin-top: 20px;
}

.pet-type-fieldset {
  min-width: 0;
  margin: 0;
  padding: 18px;
  border: 1px solid #d7dfd2;
  border-radius: 16px;
  background: #fbfcf9;
}

.pet-type-fieldset.error {
  border-color: #c43d30;
}

.pet-type-fieldset legend {
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  color: #263522;
  font-size: 18px;
  font-weight: 700;
}

.pet-type-help {
  margin: 6px 0 14px;
  color: #536051;
}

.pet-type-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.pet-type-option {
  position: relative;
  display: block;
  cursor: pointer;
}

.pet-type-radio {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.pet-type-card {
  display: flex;
  min-height: 104px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border: 2px solid #cfd8ca;
  border-radius: 14px;
  background: #ffffff;
  color: #263522;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.pet-type-option:hover .pet-type-card {
  border-color: #71806e;
}

.pet-type-radio:focus-visible + .pet-type-card {
  outline: 3px solid rgba(58, 112, 49, 0.28);
  outline-offset: 2px;
}

.pet-type-radio:checked + .pet-type-card {
  border-color: #3a7031;
  background: #f0f7ed;
  box-shadow: 0 0 0 1px #3a7031;
}

.pet-type-icon {
  font-size: 32px;
  line-height: 1;
}

.pet-type-label {
  font-size: 18px;
  font-weight: 700;
}

.pet-type-selected {
  color: #315f2a;
  font-size: 13px;
  font-weight: 700;
}

.pet-details {
  margin-top: 20px;
}

.registration-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f0f7ed;
  color: #315f2a;
}

.registration-summary-icon {
  flex: 0 0 auto;
  font-size: 26px;
}

.form-alert {
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff4e8;
  color: #8d4a09;
}

.error {
  margin-top: 6px;
  color: #c43d30;
  font-size: 13px;
}

.uk-input.error,
.uk-select.error,
.pet-type-fieldset.error {
  border-color: #c43d30;
}

@media (max-width: 639px) {
  .register-kit-card {
    padding: 22px 18px;
    border-radius: 16px;
  }

  .field-label,
  .pet-type-fieldset legend {
    flex-direction: column;
    gap: 2px;
  }

  .pet-type-fieldset {
    padding: 16px;
  }

  .pet-type-card {
    min-height: 96px;
    flex-direction: column;
    gap: 6px;
    padding: 14px 10px;
  }

  .pet-type-selected {
    min-height: 18px;
  }

  .registration-summary {
    align-items: flex-start;
  }
}

@media (max-width: 359px) {
  .pet-type-options {
    grid-template-columns: 1fr;
  }

  .pet-type-card {
    min-height: 84px;
    flex-direction: row;
  }
}
</style>
